import axios from 'axios';

// Use relative URL in production, localhost in development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tds_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh on 401 + feature-locked events
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Feature-locked response from license middleware
    if (error.response?.status === 403 && error.response?.data?.code === 'FEATURE_LOCKED') {
      const feature = error.response.data.feature || 'unknown';
      window.dispatchEvent(new CustomEvent('tds:feature-locked', {
        detail: { feature, message: error.response.data.error }
      }));
      return Promise.reject(error);
    }

    // License limit response (e.g., max campaigns reached)
    if (error.response?.status === 403 && error.response?.data?.code === 'LICENSE_LIMIT') {
      window.dispatchEvent(new CustomEvent('tds:license-limit', {
        detail: { message: error.response.data.error }
      }));
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('tds_refresh_token');
        if (!refreshToken) {
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        
        const { token, refresh_token } = response.data;
        localStorage.setItem('tds_token', token);
        localStorage.setItem('tds_refresh_token', refresh_token);
        
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('tds_token');
        localStorage.removeItem('tds_refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Campaigns
export const getCampaigns = () => api.get('/campaigns');
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const createCampaign = (data) => api.post('/campaigns', data);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id, deleteClicks = false) => {
  const url = deleteClicks ? `/campaigns/${id}?delete_clicks=true` : `/campaigns/${id}`;
  return api.delete(url);
};
export const cloneCampaign = (id) => api.post(`/campaigns/${id}/clone`);
export const copySetup = (targetCampaignId, data) => api.post(`/campaigns/${targetCampaignId}/copy-setup`, data);
export const bulkUpdateCampaigns = (data) => api.post('/campaigns/bulk', data);

// Offers
export const getOffers = (campaignId) => api.get(`/campaigns/${campaignId}/offers`);
export const createOffer = (campaignId, data) => api.post(`/campaigns/${campaignId}/offers`, data);
export const updateOffer = (id, data) => api.put(`/offers/${id}`, data);
export const deleteOffer = (id) => api.delete(`/offers/${id}`);
export const bulkUpdateOffers = (data) => api.post('/offers/bulk', data);
export const reorderOffers = (orders) => api.post('/offers/reorder', { orders });

// Rules
export const getRules = (campaignId) => api.get(`/campaigns/${campaignId}/rules`);
export const createRule = (campaignId, data) => api.post(`/campaigns/${campaignId}/rules`, data);
export const updateRule = (id, data) => api.put(`/rules/${id}`, data);
export const deleteRule = (id) => api.delete(`/rules/${id}`);

// Parameter Mappings
export const getMappings = (campaignId) => api.get(`/campaigns/${campaignId}/mappings`);
export const createMapping = (campaignId, data) => api.post(`/campaigns/${campaignId}/mappings`, data);
export const updateMapping = (id, data) => api.put(`/mappings/${id}`, data);
export const deleteMapping = (id) => api.delete(`/mappings/${id}`);

// Stats
export const getStats = (params) => {
  const config = params ? { params } : {};
  return api.get('/stats', config);
};
export const getCampaignStats = (id, params) => {
  const config = params ? { params } : {};
  return api.get(`/stats/campaign/${id}`, config);
};
export const getGlobalTrackingBreakdown = (params) => {
  const config = params ? { params } : {};
  return api.get('/stats/tracking-breakdown', config);
};
export const getHourlyStats = (params) => {
  const config = params ? { params } : {};
  return api.get('/stats/hourly', config);
};
export const getCampaignComparison = (params) => {
  const config = params ? { params } : {};
  return api.get('/stats/campaign-comparison', config);
};

// Backup/Restore v2
export const backupExportCampaign = (id) => api.get(`/backup/campaign/${id}`);
export const backupExportCampaigns = (campaignIds) => api.post('/backup/campaigns', { campaign_ids: campaignIds });
export const backupExportGroup = (groupId) => api.get(`/backup/group/${groupId}`);
export const backupExportFull = () => api.get('/backup/full');
export const backupExportTools = () => api.get('/backup/tools');
export const backupImport = (payload, mode) => api.post('/backup/import', { payload, mode });
// Campaign Groups
export const getCampaignGroups = () => api.get('/campaign-groups');
export const createCampaignGroup = (data) => api.post('/campaign-groups', data);
export const updateCampaignGroup = (id, data) => api.put(`/campaign-groups/${id}`, data);
export const deleteCampaignGroup = (id) => api.delete(`/campaign-groups/${id}`);
export const moveCampaignToGroup = (campaignId, groupId) => api.put(`/campaigns/${campaignId}/group`, { group_id: groupId });

// Users (admin only)
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Click Logs
export const getClicks = (params) => {
  const config = params ? { params } : {};
  return api.get('/clicks', config);
};

// IP Reputation / Whitelist
export const getIPReputation = () => api.get('/ip-reputation');
export const createTrustedIP = (data) => api.post('/ip-reputation', data);
export const deleteTrustedIP = (id) => api.delete(`/ip-reputation/${id}`);

// NEW: Variants Lists
export const getVariantsLists = () => api.get('/variants-lists');
export const createVariantsList = (data) => api.post('/variants-lists', data);
export const updateVariantsList = (id, data) => api.put(`/variants-lists/${id}`, data);
export const deleteVariantsList = (id) => api.delete(`/variants-lists/${id}`);

// NEW: IP Lists
export const getIPLists = () => api.get('/ip-lists');
export const createIPList = (data) => api.post('/ip-lists', data);
export const updateIPList = (id, data) => api.put(`/ip-lists/${id}`, data);
export const deleteIPList = (id) => api.delete(`/ip-lists/${id}`);

// NEW: Country Groups
export const getCountryGroups = () => api.get('/country-groups');
export const createCountryGroup = (data) => api.post('/country-groups', data);
export const updateCountryGroup = (id, data) => api.put(`/country-groups/${id}`, data);
export const deleteCountryGroup = (id) => api.delete(`/country-groups/${id}`);

// NEW: Content Templates
export const getContentTemplates = () => api.get('/content-templates');
export const createContentTemplate = (data) => api.post('/content-templates', data);
export const updateContentTemplate = (id, data) => api.put(`/content-templates/${id}`, data);
export const deleteContentTemplate = (id) => api.delete(`/content-templates/${id}`);

// NEW: Advanced Rules API
export const getAdvancedRules = (campaignId) => api.get(`/campaigns/${campaignId}/advanced-rules`);
export const createAdvancedRule = (campaignId, data) => api.post(`/campaigns/${campaignId}/advanced-rules`, data);
export const updateAdvancedRule = (id, data) => api.put(`/advanced-rules/${id}`, data);
export const deleteAdvancedRule = (id) => api.delete(`/advanced-rules/${id}`);
export const reorderAdvancedRules = (campaignId, rules) => api.post(`/campaigns/${campaignId}/advanced-rules/reorder`, { rules });

// Rule Groups (AND logic containers)
export const getRuleGroups = (campaignId) => api.get(`/campaigns/${campaignId}/rule-groups`);
export const createRuleGroup = (campaignId, data) => api.post(`/campaigns/${campaignId}/rule-groups`, data);
export const updateRuleGroup = (id, data) => api.put(`/rule-groups/${id}`, data);
export const deleteRuleGroup = (id) => api.delete(`/rule-groups/${id}`);

// NEW: Forces
export const getForces = () => api.get('/forces');
export const createForce = (data) => api.post('/forces', data);
export const updateForce = (id, data) => api.put(`/forces/${id}`, data);
export const deleteForce = (id) => api.delete(`/forces/${id}`);

// NEW: A/B Tests
export const getABTests = () => api.get('/ab-tests');
export const createABTest = (data) => api.post('/ab-tests', data);
export const getABTestResults = (id) => api.get(`/ab-tests/${id}/results`);
export const deleteABTest = (id) => api.delete(`/ab-tests/${id}`);

// NEW: Comments API
export const getOfferComments = (offerId) => api.get(`/offers/${offerId}/comments`);
export const createOfferComment = (offerId, content) => api.post(`/offers/${offerId}/comments`, { content });
export const updateOfferComment = (offerId, commentId, content) => api.put(`/offers/${offerId}/comments/${commentId}`, { content });
export const deleteOfferComment = (offerId, commentId) => api.delete(`/offers/${offerId}/comments/${commentId}`);

export const getCampaignComments = (campaignId) => api.get(`/campaigns/${campaignId}/comments`);
export const createCampaignComment = (campaignId, content) => api.post(`/campaigns/${campaignId}/comments`, { content });
export const updateCampaignComment = (campaignId, commentId, content) => api.put(`/campaigns/${campaignId}/comments/${commentId}`, { content });
export const deleteCampaignComment = (campaignId, commentId) => api.delete(`/campaigns/${campaignId}/comments/${commentId}`);

// NEW: Cache Reload
export const reloadCache = () => api.post('/admin/cache/reload');
export const getCacheStats = () => api.get('/admin/cache/stats');

// NEW: Offer Stats API
export const fetchCampaignOfferStats = (campaignId, timeframe = '7d', timeline = true) => {
  return api.get(`/campaigns/${campaignId}/offer-stats`, {
    params: { timeframe, timeline: timeline ? 'true' : 'false' }
  }).then(res => res.data);
};

export const fetchOfferTimeline = (campaignId, offerId, from, to) => {
  return api.get(`/campaigns/${campaignId}/offers/${offerId}/timeline`, {
    params: { from, to }
  }).then(res => res.data);
};

// NEW: Tracking breakdown — UTM, sub_id, custom params breakdown
export const fetchTrackingBreakdown = (campaignId, param, from, to) => {
  return api.get(`/campaigns/${campaignId}/tracking-breakdown`, {
    params: { param, from, to }
  }).then(res => res.data);
};

// ==================== Postback & Conversions ====================

// Postback Profiles
export const getPostbackProfiles = () => api.get('/postback-profiles');
export const createPostbackProfile = (data) => api.post('/postback-profiles', data);
export const updatePostbackProfile = (id, data) => api.put(`/postback-profiles/${id}`, data);
export const deletePostbackProfile = (id) => api.delete(`/postback-profiles/${id}`);

// Conversions
export const getConversions = (params) => api.get('/conversions', { params });
export const getConversionStats = (params) => api.get('/conversions/stats', { params });
export const updateConversionStatus = (id, status) => api.put(`/conversions/${id}/status`, { status });

// Exchange Rates
export const getExchangeRates = () => api.get('/exchange-rates');
export const updateExchangeRate = (currency, rate) => api.put(`/exchange-rates/${currency}`, { rate });
export const refreshExchangeRates = () => api.post('/exchange-rates/refresh');

// Settings
export const getSettings = () => api.get('/settings');
export const updateSetting = (key, value) => api.put(`/settings/${key}`, { value });

// Campaign postback profile assignment
export const assignCampaignPostbackProfile = (campaignId, profileId) =>
  api.put(`/campaigns/${campaignId}/postback-profile`, { profile_id: profileId });

// Test postback (simulate conversion for testing)
export const testPostback = (data) => api.post('/postback-test', data);

export default api;

// API Keys (programmatic click API)
export const getApiKeys = () => api.get('/api-keys');
export const createApiKey = (name) => api.post('/api-keys', { name });
export const revokeApiKey = (id) => api.delete(`/api-keys/${id}`);
